import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import {
    Switch,
    Card,
    CardContent,
    CardMedia,
    Typography,
    Box,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Edit,
    ListAlt,
    AddAlert,
    Favorite,
    FavoriteBorder,
    MoreVert,
    Delete,
    ToggleOn,
    ToggleOff,
    ContentCopy,
} from '@mui/icons-material';
import moment from 'moment';
import 'moment/locale/is';
import TextPill from 'components/TextPill/TextPill';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

const useStyles = createUseStyles({
    card: {
        width: '100%',
        margin: '0',
        transition: 'all 0.3s ease',
        borderRadius: '16px',
        border: '1px solid var(--spara-border, #e0e0e0)',
        overflow: 'visible',
        backgroundColor: 'var(--spara-surface, #ffffff)',
        position: 'relative',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            '& .infoText': {
                opacity: 1,
            },
        },
    },
    imageContainer: {
        position: 'relative',
        margin: '6px',
        marginBottom: '4px',
    },
    media: {
        height: 160,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderRadius: '10px',
    },
    activeIndicator: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: '#078f5c',
        border: '2px solid #ffffff',
        zIndex: 2,
    },
    favoriteButton: {
        position: 'absolute',
        top: '8px',
        left: '8px',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '50%',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
        },
    },
    menuButton: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        zIndex: 3,
        width: 32,
        height: 32,
        background: '#ffffff',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.28)',
        borderRadius: '50%',
        padding: 0,
        color: '#1a1a1a',
        '&:hover': {
            background: '#ffffff',
            color: '#000000',
            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.35)',
        },
        '& .MuiSvgIcon-root': {
            fontSize: 22,
            color: 'inherit',
        },
    },
    notifyButton: {
        position: 'absolute',
        top: '8px',
        right: '48px',
        zIndex: 3,
        width: 32,
        height: 32,
        background: '#ffffff',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.28)',
        borderRadius: '50%',
        padding: 0,
        color: '#1565c0',
        '&:hover': {
            background: '#ffffff',
            color: '#0d47a1',
            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.35)',
        },
        '& .MuiSvgIcon-root': {
            fontSize: 20,
            color: 'inherit',
        },
    },
    separator: {
        height: '12px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        overflow: 'visible',
    },
    punchOut: {
        position: 'absolute',
        height: '12px',
        width: '12px',
        backgroundColor: 'var(--spara-bg, #ffffff)',
        borderRadius: '50%',
        border: '1px solid var(--spara-border, #e0e0e0)',
        zIndex: 1,
    },
    punchOutLeft: {
        left: '-6px',
    },
    punchOutRight: {
        right: '-6px',
    },
    punchOutMiddle: {
        height: '4px',
        width: '4px',
        backgroundColor: 'var(--spara-border, #E5E6E8)',
        borderRadius: '50%',
        margin: '0 2px',
    },
    content: {
        padding: '5px 8px 0px 8px',
        paddingBottom: '10px !important',
        flexGrow: 1,
    },
    discountContainer: {
        backgroundColor: 'var(--spara-offer-well, #E7F7FD)',
        borderRadius: '8px',
        padding: '4px 6px',
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    discountText: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: 'var(--spara-primary, #d32f2f)',
        lineHeight: '36px',
        flex: 1,
    },
    specialDiscountText: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: 'var(--spara-offer-well-text, #005273)',
        lineHeight: '36px',
        flex: 1,
    },
    discountSubText: {
        fontSize: '14px',
        lineHeight: '24px',
        color: 'var(--spara-primary, #d32f2f)',
    },
    specialOfferText: {
        fontSize: '10px',
        fontWeight: '500',
        color: 'var(--spara-offer-well-text, #005273)',
        lineHeight: '15px',
        marginBottom: '2px',
    },
    title: {
        fontSize: '14px',
        fontWeight: '600',
        lineHeight: '21px',
        color: 'var(--spara-text-strong, #282929)',
        margin: '0 8px 16px 8px',
        minHeight: '42px',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    companyContainer: {
        display: 'flex',
        alignItems: 'center',
        marginTop: '5px',
        marginBottom: '8px',
        marginLeft: '8px',
        marginRight: '8px',
    },
    companyLogo: {
        width: '20px',
        height: '20px',
        borderRadius: '10px',
        marginRight: '8px',
        backgroundColor: 'var(--spara-surface-2, #f5f5f5)',
        objectFit: 'cover',
    },
    companyName: {
        flex: 1,
        fontSize: '12px',
        lineHeight: '18px',
        color: 'var(--spara-text-muted, #666)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    statusContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginBottom: '8px',
    },
    categories: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        marginBottom: '8px',
    },
    categoryIcon: {
        width: '16px',
        height: '16px',
        borderRadius: '2px',
    },
    newIndicator: {
        position: 'absolute',
        top: '8px',
        right: '40px',
        backgroundColor: '#ff9800',
        color: '#ffffff',
        fontSize: '10px',
        padding: '2px 6px',
        borderRadius: '4px',
        zIndex: 2,
    },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
            'linear-gradient(135deg, rgba(211, 47, 47, 0.1) 0%, rgba(211, 47, 47, 0.3) 100%)',
        pointerEvents: 'none',
        zIndex: 1,
        borderRadius: '16px',
    },
    statusContainer: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
    },
    statusText: {
        backgroundColor: 'rgba(211, 47, 47, 0.9)',
        color: 'white',
        fontWeight: 'bold',
        padding: '8px 16px',
        borderRadius: '20px',
        fontSize: '0.9rem',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    infoText: {
        position: 'absolute',
        top: -1,
        left: -1,
        zIndex: 3,
        color: 'white',
        backgroundColor: 'rgba(211, 47, 47, 0.9)',
        padding: '4px 12px',
        borderRadius: '12px 0px 12px 0',
        fontSize: '0.8rem',
        textAlign: 'center',
        fontWeight: 600,
        maxWidth: '180px',
        lineHeight: '1.2',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        pointerEvents: 'none',
    },
    disabledCard: {
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow:
                '0 4px 20px 0 rgba(211, 47, 47, 0.14), 0 7px 10px -5px rgba(211, 47, 47, 0.2)',
            '& .infoText': {
                opacity: 1,
            },
        },
    },
    dateContainer: {
        marginTop: '8px',
        paddingTop: '8px',
        borderTop: '1px solid var(--spara-border, #f0f0f0)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '0.75rem',
        color: 'var(--spara-text-muted, #666)',
    },
    dateText: {
        fontSize: '0.75rem',
        color: 'var(--spara-text-muted, #666)',
        fontWeight: 400,
    },
    openToAllPill: {
        backgroundColor: '#078f5c',
        color: 'white',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: 600,
        display: 'inline',
        marginTop: '4px',
    },
    actionButton: {
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        color: '#666',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
        },
    },
});

const OfferCard = ({
    offer,
    dataGroup,
    sort,
    updateButton,
    reportButton,
    deleteButton,
    changeOfferActive,
    notificationButton,
    canManageOffer,
    onDuplicate,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const menuOpen = Boolean(menuAnchor);
    const [noAccessDialog, setNoAccessDialog] = useState(null); // 'edit' | 'statistics' | null
    const groups = Array.isArray(dataGroup) ? dataGroup : [];

    const userStore = JSON.parse(localStorage.getItem('user') || '{}');
    const userGroupIds = (userStore.groupIds || []).map(Number).filter(Boolean);
    const roles = userStore.roles || [];
    // Pure group admins should not see how many other groups share an offer.
    const hideOtherGroupsCount =
        roles.includes('group_admin') && !roles.includes('company_user');

    const prioritizeUserGroupIds = (ids) => {
        const normalized = ids.map(Number).filter(Boolean);
        return [
            ...normalized.filter((id) => userGroupIds.includes(id)),
            ...normalized.filter((id) => !userGroupIds.includes(id)),
        ];
    };

    const handleMenuOpen = (e) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
    };
    const handleMenuClose = (e) => {
        if (e) {
            e.stopPropagation();
        }
        setMenuAnchor(null);
    };

    const getOfferImage = () => {
        if (offer.offer_images && offer.offer_images.length > 0) {
            return offer.offer_images[0].image;
        }
        return offer.image || '/spara-placeholder.png';
    };

    const getStatusInfo = () => {
        const isApproved = offer.approved;
        const isActive = offer.is_active;
        const isDisplayed = isApproved && isActive;

        return {
            status: isApproved ? 'Samþykkt' : 'Í rýni',
            statusColor: isApproved ? '#078f5c' : '#0f8cbd',
            displayStatus: isDisplayed ? 'Í birtingu' : 'Ekki í birtingu',
            displayColor: isDisplayed ? '#078f5c' : '#0f8cbd',
        };
    };

    const getSpecialCompanyInfo = () => {
        if (offer.group_ids) {
            const sortedIds = prioritizeUserGroupIds(offer.group_ids.split(','));
            const specialNames = sortedIds
                .map((item) => groups.find((spec) => Number(spec.id) === item)?.name)
                .filter(Boolean);

            if (specialNames.length > 0) {
                const preferredId =
                    sortedIds.find((id) => {
                        const spec = groups.find((s) => Number(s.id) === id);
                        return spec?.logo;
                    }) ?? sortedIds[0];
                return {
                    name: specialNames.join(', '),
                    logo: groups.find((spec) => Number(spec.id) === preferredId)?.logo,
                };
            } else {
                return { name: 'Óþekktur hópur', logo: null };
            }
        } else if (offer.torgid_special_company) {
            return {
                name: offer.torgid_special_company.name,
                logo: offer.torgid_special_company.logo,
            };
        } else {
            return { name: 'Almennir notendur', logo: null };
        }
    };

    const isNewOffer = () => {
        const startDate = moment(offer.start_date);
        const now = moment();
        return startDate.diff(now, 'days') >= -7 && startDate.diff(now, 'days') <= 0;
    };

    // Ákveða hvort overlay birtist og hvaða texta sýna
    const getOverlayInfo = () => {
        if (!offer.approved) {
            return {
                show: true,
                text: t('offer.in_review'),
                color: '#1976d2',
                info: t('offer.not_displayed'),
            };
        } else if (offer.approved && !offer.is_active) {
            return {
                show: true,
                text: t('offer.inactive'),
                color: '#d32f2f',
                info: t('offer.not_displayed'),
            };
        }
        return { show: false, text: '', color: '', info: '' };
    };

    const overlayInfo = getOverlayInfo();
    const canManage = canManageOffer ? canManageOffer(offer) : true;

    const handleCardClick = (e) => {
        if (!updateButton) return;
        if (canManage) {
            if (e.metaKey || e.ctrlKey) {
                window.open(`/offers/${offer.id}`, '_blank');
            } else {
                navigate(`/offers/${offer.id}`);
            }
        } else {
            setNoAccessDialog('edit');
        }
    };

    return (
        <>
        <ConfirmDialog
            open={Boolean(noAccessDialog)}
            type="info"
            title={t(
                noAccessDialog === 'statistics'
                    ? 'offer.no_access_stats_title'
                    : 'offer.no_access_title'
            )}
            message={t(
                noAccessDialog === 'statistics'
                    ? 'offer.no_access_stats_message'
                    : 'offer.no_access_message'
            )}
            onConfirm={() => setNoAccessDialog(null)}
            confirmText={t('close')}
        />
        <Card
            className={`${classes.card} ${overlayInfo.show ? classes.disabledCard : ''}`}
            onClick={handleCardClick}
            style={{ cursor: 'pointer' }}
        >
            {overlayInfo.show && (
                <Box
                    className={classes.overlay}
                    style={{
                        background:
                            overlayInfo.color === '#1976d2'
                                ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.3) 100%)'
                                : 'linear-gradient(135deg, rgba(211, 47, 47, 0.1) 0%, rgba(211, 47, 47, 0.3) 100%)',
                    }}
                />
            )}
            <Box className={classes.imageContainer}>
                <CardMedia className={classes.media} image={getOfferImage()} title={offer.title} />

                {notificationButton && notificationButton(offer) && (
                    <IconButton
                        className={classes.notifyButton}
                        size="small"
                        title={t('send_notification')}
                        data-testid={
                            offer.is_active && offer.approved && offer.notified
                                ? `offer-card-notify-retry-${offer.id}`
                                : offer.is_active && offer.approved && !offer.notified
                                  ? `offer-card-notify-${offer.id}`
                                  : `offer-card-notify-disabled-${offer.id}`
                        }
                        onClick={(e) => {
                            e.stopPropagation();
                            const btn = notificationButton(offer);
                            if (btn?.props?.onClick) btn.props.onClick(e);
                        }}
                    >
                        <AddAlert />
                    </IconButton>
                )}
                {/* Dropdown actions */}
                <IconButton
                    className={classes.menuButton}
                    onClick={handleMenuOpen}
                    size="small"
                    data-testid={`offer-card-menu-${offer.id}`}
                >
                    <MoreVert />
                </IconButton>
                <Menu
                    anchorEl={menuAnchor}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <MenuItem
                        onClick={() => {
                            handleMenuClose();
                            if (canManage) {
                                changeOfferActive(offer);
                            } else {
                                setNoAccessDialog('edit');
                            }
                        }}
                        style={!canManage ? { opacity: 0.5 } : undefined}
                    >
                        <ListItemIcon>
                            {offer.is_active ? (
                                <ToggleOn color={canManage ? 'success' : 'action'} />
                            ) : (
                                <ToggleOff color="action" />
                            )}
                        </ListItemIcon>
                        <ListItemText>{offer.is_active ? t('disable') : t('enable')}</ListItemText>
                    </MenuItem>
                    {updateButton && canManage && (
                        <MenuItem
                            component="a"
                            href={`/offers/${offer.id}`}
                            onClick={(e) => {
                                if (!e.metaKey && !e.ctrlKey) {
                                    e.preventDefault();
                                    handleMenuClose();
                                    navigate(`/offers/${offer.id}`);
                                } else {
                                    handleMenuClose();
                                }
                            }}
                        >
                            <ListItemIcon>
                                <Edit />
                            </ListItemIcon>
                            <ListItemText>{t('Breyta')}</ListItemText>
                        </MenuItem>
                    )}
                    {onDuplicate && canManage && (
                        <MenuItem
                            onClick={() => {
                                handleMenuClose();
                                onDuplicate(offer);
                            }}
                        >
                            <ListItemIcon>
                                <ContentCopy />
                            </ListItemIcon>
                            <ListItemText>{t('offer.duplicate')}</ListItemText>
                        </MenuItem>
                    )}
                    {onDuplicate && !canManage && (
                        <MenuItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleMenuClose();
                                setNoAccessDialog('edit');
                            }}
                            style={{ opacity: 0.5 }}
                        >
                            <ListItemIcon>
                                <ContentCopy />
                            </ListItemIcon>
                            <ListItemText>{t('offer.duplicate')}</ListItemText>
                        </MenuItem>
                    )}
                    {updateButton && !canManage && (
                        <MenuItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleMenuClose();
                                setNoAccessDialog('edit');
                            }}
                            style={{ opacity: 0.5 }}
                        >
                            <ListItemIcon>
                                <Edit />
                            </ListItemIcon>
                            <ListItemText>{t('Breyta')}</ListItemText>
                        </MenuItem>
                    )}
                    {deleteButton && canManage && (
                        <MenuItem
                            onClick={() => {
                                handleMenuClose();
                                deleteButton(offer).props.onClick();
                            }}
                        >
                            <ListItemIcon>
                                <Delete />
                            </ListItemIcon>
                            <ListItemText>{t('Eyða')}</ListItemText>
                        </MenuItem>
                    )}
                    {notificationButton && notificationButton(offer) && (
                        <MenuItem
                            data-testid={`offer-card-menu-notify-${offer.id}`}
                            onClick={() => {
                                handleMenuClose();
                                notificationButton(offer).props.onClick();
                            }}
                        >
                            <ListItemIcon>
                                <AddAlert />
                            </ListItemIcon>
                            <ListItemText>{t('send_notification')}</ListItemText>
                        </MenuItem>
                    )}
                    {reportButton && canManage && (
                        <MenuItem
                            component="a"
                            href={`/offers/${offer.id}/statistics`}
                            onClick={(e) => {
                                if (!e.metaKey && !e.ctrlKey) {
                                    e.preventDefault();
                                    handleMenuClose();
                                    navigate(`/offers/${offer.id}/statistics`);
                                } else {
                                    handleMenuClose();
                                }
                            }}
                        >
                            <ListItemIcon>
                                <ListAlt />
                            </ListItemIcon>
                            <ListItemText>{t('Tölfræði')}</ListItemText>
                        </MenuItem>
                    )}
                    {reportButton && !canManage && (
                        <MenuItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleMenuClose();
                                setNoAccessDialog('statistics');
                            }}
                            style={{ opacity: 0.5 }}
                        >
                            <ListItemIcon>
                                <ListAlt />
                            </ListItemIcon>
                            <ListItemText>{t('Tölfræði')}</ListItemText>
                        </MenuItem>
                    )}
                </Menu>

                {isNewOffer() && <Box className={classes.newIndicator}>NÝTT</Box>}
            </Box>
            {overlayInfo.show && (
                <>
                    <Box className={classes.statusContainer}>
                        <Typography
                            className={classes.statusText}
                            style={{
                                backgroundColor:
                                    overlayInfo.color === '#1976d2'
                                        ? 'rgba(25, 118, 210, 0.9)'
                                        : 'rgba(211, 47, 47, 0.9)',
                            }}
                        >
                            {overlayInfo.text}
                        </Typography>
                    </Box>
                    <Typography
                        className={classes.infoText}
                        style={{
                            backgroundColor:
                                overlayInfo.color === '#1976d2'
                                    ? 'rgba(25, 118, 210, 0.9)'
                                    : 'rgba(211, 47, 47, 0.9)',
                        }}
                    >
                        {overlayInfo.info}
                    </Typography>
                </>
            )}

            <Box className={classes.separator}>
                <Box
                    style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'center',

                        marginLeft: '6px',
                        marginRight: '6px',
                    }}
                >
                    {Array.from(Array(20)).map((_, index) => (
                        <Box key={index} className={classes.punchOutMiddle} />
                    ))}
                </Box>
            </Box>

            <CardContent className={classes.content}>
                {/* Discount Section */}
                {(offer.group_ids || offer.torgid_special_company) && offer.discount && (
                    <Box className={classes.discountContainer}>
                        <Box style={{ flex: 1 }}>
                            {!['sérkjör', 'kynning', 'tilkynning', 'skráning', '2 fyrir 1'].includes(
                                offer.discount.toLowerCase()
                            ) && (
                                <Typography className={classes.specialOfferText}>
                                    {t('offer.special_offer')}
                                </Typography>
                            )}
                            <Typography className={classes.specialDiscountText}>
                                {['sérkjör', 'kynning', 'tilkynning', 'skráning', '2 fyrir 1'].includes(
                                    offer.discount.toLowerCase()
                                )
                                    ? offer.discount.charAt(0).toUpperCase() +
                                      offer.discount.slice(1).toLowerCase()
                                    : offer.discount}
                            </Typography>
                        </Box>
                        <Box
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px',
                            }}
                        >
                            {/* group_ids — prioritize groups the user controls (e.g. company_user + group access) */}
                            {offer.group_ids &&
                                (() => {
                                    const sortedIds = prioritizeUserGroupIds(
                                        offer.group_ids.split(',')
                                    );

                                    const firstGroupWithLogo = sortedIds.find((id) => {
                                        const spec = groups.find((s) => Number(s.id) === id);
                                        return spec && spec.logo;
                                    });

                                    if (firstGroupWithLogo) {
                                        const spec = groups.find(
                                            (s) => Number(s.id) === firstGroupWithLogo
                                        );
                                        return (
                                            <img
                                                key={spec.id}
                                                src={spec.logo}
                                                alt={spec.name}
                                                style={{
                                                    width: '30px',
                                                    height: '30px',
                                                    borderRadius: '300px',
                                                    objectFit: 'cover',
                                                    border: '1px solid white',
                                                    backgroundColor: '#fff',
                                                }}
                                                title={spec.name}
                                                onError={(e) => {
                                                    e.target.src = '/spara-placeholder.png';
                                                }}
                                            />
                                        );
                                    }
                                    return null;
                                })()}
                            {/* Show remaining count if more than 1 groups */}
                            {!hideOtherGroupsCount &&
                                offer.group_ids &&
                                offer.group_ids.split(',').length > 1 && (
                                    <Typography
                                        style={{
                                            fontSize: '0.75rem',
                                            color: 'var(--spara-text-muted, #666)',
                                            marginLeft: '4px',
                                        }}
                                    >
                                        +{offer.group_ids.split(',').length - 1}
                                    </Typography>
                                )}
                            {/* torgid_special_company ef ekki í group_ids */}
                            {!offer.group_ids && offer.torgid_special_company && (
                                <img
                                    src={
                                        offer.torgid_special_company.logo ||
                                        '/spara-placeholder.png'
                                    }
                                    alt={offer.torgid_special_company.name}
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '3px',
                                        objectFit: 'cover',
                                        border: '1px solid white',
                                    }}
                                    title={offer.torgid_special_company.name}
                                    onError={(e) => {
                                        e.target.src = '/spara-placeholder.png';
                                    }}
                                />
                            )}
                        </Box>
                        {offer.special_discount && (
                            <Box
                                style={{
                                    paddingHorizontal: '9px',
                                    borderRadius: '6px',
                                    backgroundColor: '#f0f8ff',
                                    aspectRatio: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexShrink: 1,
                                }}
                            >
                                <Typography
                                    style={{
                                        color: '#1976d2',
                                        opacity: 0.5,
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        textDecoration: 'line-through',
                                    }}
                                >
                                    {offer.discount}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {!offer.group_ids && !offer.torgid_special_company && offer.discount && (
                    <Typography
                        className={classes.discountText}
                        style={{ marginHorizontal: '8px' }}
                    >
                        {['sérkjör', 'kynning', 'tilkynning', 'skráning', '2 fyrir 1'].includes(
                            offer.discount.toLowerCase()
                        )
                            ? offer.discount.charAt(0).toUpperCase() +
                              offer.discount.slice(1).toLowerCase()
                            : offer.discount}
                    </Typography>
                )}

                <Typography className={classes.title}>{offer.title}</Typography>

                <Box style={{ flex: 1 }} />

                <Box>
                    {/* Fyrirtækjalógó og nafn */}
                    <Box className={classes.companyContainer}>
                        <img
                            src={offer.torgid_company?.logo || '/spara-placeholder.png'}
                            alt={offer.torgid_company?.name || 'Company'}
                            className={classes.companyLogo}
                            title={offer.torgid_company?.name || 'Company'}
                            onError={(e) => {
                                e.target.src = '/spara-placeholder.png';
                            }}
                        />
                        {offer.torgid_company?.name && (
                            <Typography className={classes.companyName}>
                                {offer.torgid_company.name}
                            </Typography>
                        )}
                    </Box>

                    {sort === 'activity' && (
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                if (canManage && reportButton) {
                                    navigate(`/offers/${offer.id}/statistics`);
                                } else if (!canManage) {
                                    setNoAccessDialog('statistics');
                                }
                            }}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-around',
                                borderTop: '1px solid var(--spara-border, #f0f0f0)',
                                marginTop: 6,
                                paddingTop: 6,
                                cursor: canManage && reportButton ? 'pointer' : 'default',
                            }}
                        >
                            <Box style={{ textAlign: 'center' }}>
                                <Typography style={{ fontSize: 13, fontWeight: 600, color: '#16B4F3' }}>
                                    {offer.opens_count ?? 0}
                                </Typography>
                                <Typography style={{ fontSize: 10, color: 'var(--spara-text-muted, #999)' }}>
                                    {t('opened')}
                                </Typography>
                            </Box>
                            <Box style={{ textAlign: 'center' }}>
                                <Typography style={{ fontSize: 13, fontWeight: 600, color: '#008B6B' }}>
                                    {offer.activates_count ?? 0}
                                </Typography>
                                <Typography style={{ fontSize: 10, color: 'var(--spara-text-muted, #999)' }}>
                                    {t('activated')}
                                </Typography>
                            </Box>
                            <Box style={{ textAlign: 'center' }}>
                                <Typography style={{ fontSize: 13, fontWeight: 600, color: '#DE2C48' }}>
                                    {offer.favourites_count ?? 0}
                                </Typography>
                                <Typography style={{ fontSize: 10, color: 'var(--spara-text-muted, #999)' }}>
                                    {t('favourited')}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
        </>
    );
};

export default OfferCard;
